import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RotateCw, 
  ArrowLeft, 
  Check, 
  X, 
  Loader2, 
  Image as ImageIcon,
  Sparkles,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const Admin360Views = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    // Check admin auth
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadProducts();
  }, [navigate]);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const productsData = response.data.products || [];
      
      // For each product, check if it has 360 views
      const productsWithViewStatus = await Promise.all(
        productsData.map(async (product) => {
          try {
            const viewsResponse = await axios.get(`${API}/products/${product.id}/360-views`);
            return {
              ...product,
              has360View: viewsResponse.data.has_360_view,
              viewsCount: viewsResponse.data.views?.length || 0
            };
          } catch {
            return { ...product, has360View: false, viewsCount: 0 };
          }
        })
      );
      
      setProducts(productsWithViewStatus);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Fout bij laden van producten');
    } finally {
      setLoading(false);
    }
  };

  const generate360Views = async (productId) => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      toast.error('Niet ingelogd als admin');
      return;
    }

    setGenerating(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await axios.post(
        `${API}/ai/generate-360-views`,
        { product_id: productId },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`${response.data.views?.length || 4} views gegenereerd!`);
      
      // Update product in list
      setProducts(prev => prev.map(p => 
        p.id === productId 
          ? { ...p, has360View: true, viewsCount: response.data.views?.length || 4 }
          : p
      ));
    } catch (error) {
      console.error('Error generating 360 views:', error);
      // Handle error message properly - it might be an array or object
      let message = 'Fout bij genereren';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          message = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          message = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(message);
    } finally {
      setGenerating(prev => ({ ...prev, [productId]: false }));
    }
  };

  const generateAllViews = async () => {
    const productsWithoutViews = products.filter(p => !p.has360View);
    
    if (productsWithoutViews.length === 0) {
      toast.info('Alle producten hebben al 360° views');
      return;
    }

    setBulkGenerating(true);
    let completed = 0;
    const total = productsWithoutViews.length;

    for (const product of productsWithoutViews) {
      toast.info(`Genereren ${completed + 1}/${total}: ${product.name}...`);
      await generate360Views(product.id);
      completed++;
      // Small delay between generations to avoid rate limits
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    setBulkGenerating(false);
    toast.success(`Klaar! ${completed} producten verwerkt.`);
  };

  const productsWithViews = products.filter(p => p.has360View).length;
  const productsWithoutViews = products.filter(p => !p.has360View).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#2C1B18] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <RotateCw className="text-[#D4AF37]" size={24} />
                360° Product Views
              </h1>
              <p className="text-[#A8A29E] text-sm">
                Genereer AI-gestuurde 360° weergaven voor producten
              </p>
            </div>
          </div>
          
          <button
            onClick={generateAllViews}
            disabled={bulkGenerating || productsWithoutViews === 0}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="generate-all-btn"
          >
            {bulkGenerating ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Sparkles size={18} />
            )}
            Genereer Alle ({productsWithoutViews})
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-6 py-4 grid grid-cols-3 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{products.length}</p>
          <p className="text-[#A8A29E] text-sm">Totaal Producten</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">{productsWithViews}</p>
          <p className="text-[#A8A29E] text-sm">Met 360° Views</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-500">{productsWithoutViews}</p>
          <p className="text-[#A8A29E] text-sm">Zonder 360° Views</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="px-6 pb-4">
        <div className="glass rounded-xl p-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-white font-medium">Over 360° AI Generatie</p>
              <p className="text-[#A8A29E] text-sm mt-1">
                Deze functie genereert 4 productfoto&apos;s vanuit verschillende hoeken (0°, 90°, 180°, 270°) 
                met een elegante achtergrond. Het genereren kan enkele seconden per foto duren.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="px-6 pb-8">
        <div className="grid gap-4">
          {products.map((product) => (
            <div 
              key={product.id}
              className="glass rounded-xl p-4 flex items-center gap-4"
              data-testid={`product-row-${product.id}`}
            >
              {/* Product Image */}
              <div className="w-20 h-20 bg-[#121212] rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium truncate">{product.name}</h3>
                <p className="text-[#D4AF37] text-sm">{product.brand}</p>
                <p className="text-[#57534E] text-xs">{product.category === 'men' ? 'Heren' : 'Dames'}</p>
              </div>

              {/* 360 Status */}
              <div className="flex items-center gap-4">
                {product.has360View ? (
                  <div className="flex items-center gap-2 text-green-500">
                    <Check size={20} />
                    <span className="text-sm">{product.viewsCount} views</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[#57534E]">
                    <X size={20} />
                    <span className="text-sm">Geen views</span>
                  </div>
                )}

                {/* Generate Button */}
                <button
                  onClick={() => generate360Views(product.id)}
                  disabled={generating[product.id] || bulkGenerating}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    product.has360View
                      ? 'bg-[#2C1B18] text-[#A8A29E] hover:bg-[#3C2B28]'
                      : 'bg-[#D4AF37] text-black hover:bg-[#E5C048]'
                  } disabled:opacity-50`}
                  data-testid={`generate-btn-${product.id}`}
                >
                  {generating[product.id] ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Bezig...</span>
                    </>
                  ) : (
                    <>
                      <ImageIcon size={16} />
                      <span>{product.has360View ? 'Opnieuw' : 'Genereer'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin360Views;
